import React, { useContext, useEffect, useState } from "react";
import { PortalContext } from "App";
import { useTranslation } from "react-i18next";
import s from "./OfflineMerchantProcessorAccounts.module.scss";
import NoticeBox from "components/NoticeBox/NoticeBox";
import {
  getProcessorOnboardStatus,
  postProcessorOnboardMerchant,
  delProcessorOffboardMerchant,
  checkAllinPayMultipleMid,
  batchUpdateGsi,
} from "./model";
import Datatable from "components/Datatable/Datatable";
import { Button, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import {
  checkNonMoneyDataForTable,
  getCardTypeIcon,
  ProcessorType,
} from "utils/utils";
import { InfoOutlined, ContentCopy } from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import StatusWrapper from "components/StatusWrapper/StatusWrapper";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { getStatusVariant } from "components/StatusWrapper";
import TableFooter from "components/TableFooter/TableFooter";
import useSnackBar from "utils/useActionSnackBar/useActionSnackBar";

const PAGE_LOCALE_KEY =
  "page.merchants.merchantDetail.offlineMerchantProcessorAccounts.";
const config = require(
  `../../../../config/${process.env.REACT_APP_ENV}/env.json`,
);
import { spinning } from "../offline-merchant-detail/utils";

const initialDialogState = {
  open: false,
  content: null,
  title: null,
  positiveAction: null,
  negativeAction: null,
};

const initialOffboardDialogState = {
  open: false,
  content: null,
  title: null,
  positiveAction: null,
  negativeAction: null,
};

const processorAccountsCache = {};
const OfflineMerchantProcessorAccounts = ({
  path,
  merchantId,
  fetchMerchantDetail,
}) => {
  const { t } = useTranslation();
  const { actionSnackBar } = useSnackBar(PAGE_LOCALE_KEY);
  const stateContext = useContext(PortalContext);
  const { detailPageState, setDetailPageState } = stateContext;
  const [statusList, setStatusList] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState("");
  const [isAccountActionLoading, setIsAccountActionLoading] = useState({});
  const [dialogParams, setDialogParams] = useState(initialDialogState);
  const [isMultipleAipMerchant, setIsMultipleAipMerchant] = useState(false);
  const [amexMode, setAmexMode] = useState("");
  const [selectedReinstateType, setSelectedReinstateType] =
    useState("amex-mid-sme");
  const [mid, setMid] = useState("");
  const [isDialogLoading, setIsDialogLoading] = useState(false);

  const [offboardDialogParams, setOffboardDialogParams] = useState(
    initialOffboardDialogState,
  );
  const [offboardTarget, setOffboardTarget] = useState(null);
  const [isOffboardDialogLoading, setIsOffboardDialogLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const formatTime = (timestamp) =>
    new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  const NOT_ALLOW_EDIT_PROCESSOR = [
    ProcessorType.ALIPAY,
    ProcessorType.AMEX_BBMSL,
  ];
  const NOT_ALLOW_ONBOARD_PROCESSOR = [
    ProcessorType.OCTOPUS_BBMSL,
    ProcessorType.OCTOPUS_HKBN,
    ProcessorType.AMEX,
  ];
  const FOOTER_SECTIONS = [
    {
      key: 1,
      label: t(PAGE_LOCALE_KEY + "footer.editMidNotSupported"),
      processors: NOT_ALLOW_EDIT_PROCESSOR,
    },
    {
      key: 2,
      label: t(PAGE_LOCALE_KEY + "footer.pleaseOnboardThroughEnableButton"),
      processors: NOT_ALLOW_ONBOARD_PROCESSOR,
    },
    {
      key: 3,
      label: t(PAGE_LOCALE_KEY + "footer.actionsNotSupported"),
      processors: [ProcessorType.FUBON],
      cardTypes: ["UNIONPAY"],
    },
  ];

  // Utilities
  const getRowUniqueId = (row) => {
    const processor = row?.processor || "";
    const normalizedCardTypes = Array.isArray(row?.cardTypes)
      ? [...new Set(row.cardTypes)].sort().join("|")
      : "";
    return `${processor}::${normalizedCardTypes}`;
  };

  const getApplicableAnnotations = (row) => {
    const cardTypes = checkNonMoneyDataForTable(row, "cardTypes");
    return FOOTER_SECTIONS.filter((section) => {
      const processorMatch = (section.processors || []).includes(row.processor);
      if (!processorMatch) return false;
      if (section.cardTypes && section.cardTypes.length > 0) {
        return section.cardTypes.some((cardType) =>
          cardTypes.includes(cardType),
        );
      }
      return true;
    }).map((section) => section.key);
  };

  // Derive current AMEX mode from a given processor status list
  const deriveAmexMode = (processorStatusList) => {
    if (!Array.isArray(processorStatusList)) return "";
    const hasOnboarded = (processorType) =>
      processorStatusList.some(
        (item) =>
          item.processor === processorType && item?.status === "ONBOARDED",
      );
    if (hasOnboarded(ProcessorType.AMEX_BBMSL)) return ProcessorType.AMEX_BBMSL;
    if (hasOnboarded(ProcessorType.AMEX)) return ProcessorType.AMEX;
    return "";
  };

  // Helper to trigger MDR refresh via detailPageState
  const notifyMdrRefresh = () => {
    const timestamp = Date.now();
    setDetailPageState((prev) => ({
      ...prev,
      [path]: {
        ...prev[path],
        processorAccountsRefreshTrigger: timestamp,
      },
    }));
  };

  const columns = [
    {
      key: "processor",
      title: t(PAGE_LOCALE_KEY + "columnTitle.processor"),
      sorter: false,
      render: (data, key) => {
        const processor = checkNonMoneyDataForTable(data, key);
        const applicableAnnotations = getApplicableAnnotations(data);

        if (!applicableAnnotations.length) return processor;
        return (
          <span className={s.processorWithAnno}>
            <span className={s.processorText}>{processor}</span>
            {applicableAnnotations.map((key) => (
              <sup key={key} className={s.processorAnno}>
                [{key}]
              </sup>
            ))}
          </span>
        );
      },
    },
    {
      key: "cardTypes",
      title: t(PAGE_LOCALE_KEY + "columnTitle.supportedCardType"),
      sorter: false,
      render: (data) => handleDisplayCardTypes(data),
    },
    {
      key: "mid",
      title: t(PAGE_LOCALE_KEY + "columnTitle.processorMid"),
      sorter: false,
      render: (data, key) => {
        if (
          data?.processor === ProcessorType.ALLINPAY &&
          isMultipleAipMerchant
        ) {
          return (
            <div className={s.midContainer}>
              <span>
                {t(PAGE_LOCALE_KEY + "extraInfo.isMultipleAipMerchant")}
              </span>
            </div>
          );
        }
        const midValue = data[key];
        const uniqueId = getRowUniqueId(data);
        if (isEditing === uniqueId) {
          return (
            <div className={s.midContainer}>
              <OutlinedText
                defaultValue={midValue}
                noErrorMessage={true}
                onchangeFn={(e) => setMid(e.target.value)}
                divStyle={{ width: "100%" }}
              />
            </div>
          );
        }
        if (!midValue || midValue === "-") {
          return (
            <div className={s.midContainer}>
              {checkNonMoneyDataForTable(data, key)}
            </div>
          );
        }
        return (
          <div className={s.midContainer}>
            <span>{midValue}</span>
            <ContentCopy
              className={s.copyIcon}
              onClick={() => handleCopyToClipboard(midValue, data.processor)}
              title="Click to copy MID"
              style={{ cursor: "pointer" }}
            />
          </div>
        );
      },
    },
    {
      key: "status",
      title: t(PAGE_LOCALE_KEY + "columnTitle.status"),
      sorter: false,
      render: (data, key) => {
        if (data.failureReason) {
          return (
            <StatusWrapper
              status={data[key]}
              icon={<InfoOutlined sx={{ fontSize: "18px" }} />}
              variant={getStatusVariant(data[key])}
              clickable={true}
              onClick={() => handleShowFailureReasonDialog(data)}
            />
          );
        }
        return (
          <StatusWrapper
            status={data[key]}
            variant={getStatusVariant(data[key])}
          />
        );
      },
    },
    {
      key: "extraInfo",
      title: t(PAGE_LOCALE_KEY + "columnTitle.extraInfo"),
      sorter: false,
      render: (data, key) => {
        const extraInfoContent = data[key];
        if (extraInfoContent) {
          if (data.status !== "OFFBOARDED") {
            return (
              <button
                className={s.extraInfoContainer}
                onClick={() => handleShowExtraInfoDialog(data)}
              >
                {t(PAGE_LOCALE_KEY + "button.show")}
              </button>
            );
          }
        }
        return <div className={s.emptyContainer}>-</div>;
      },
    },
    {
      key: "action",
      title: t(PAGE_LOCALE_KEY + "columnTitle.action"),
      sorter: false,
      render: (data) => handleActionBtn(data),
    },
  ].map((column, index) => ({ ...column, index: index }));

  const handleCopyToClipboard = async (text, processor) => {
    try {
      await navigator.clipboard.writeText(text);
      actionSnackBar("copyMid", "success", { processor });
    } catch (error) {
      actionSnackBar("copyMid", "failed", {
        processor,
        errorMessage: error?.message ?? "unknown error",
      });
    }
  };

  const businessPlanMapper = {
    sme: "SME",
    normal: "Standard",
  };

  const generateExtraInfoTable = (data) => {
    const shouldShowBusinessPlan = data.some(
      (item) =>
        !["ALIPAY", "WECHAT", "PAYME", "OCTOPUS"].includes(item.cardType),
    );
    const extraInfoColumns = [
      {
        key: "cardType",
        title: t(PAGE_LOCALE_KEY + "extraInfo.cardType"),
        sorter: false,
        render: (data) => {
          return checkNonMoneyDataForTable(data, "cardType");
        },
      },
      {
        key: "mid",
        title: t(PAGE_LOCALE_KEY + "extraInfo.mid"),
        sorter: false,
        render: (data) => {
          return checkNonMoneyDataForTable(data, "mid");
        },
      },
      {
        key: "mcc",
        title: t(PAGE_LOCALE_KEY + "extraInfo.mcc"),
        sorter: false,
        render: (data) => {
          return checkNonMoneyDataForTable(data, "mcc");
        },
      },
    ];

    if (shouldShowBusinessPlan) {
      extraInfoColumns.push({
        key: "businessPlan",
        title: t(PAGE_LOCALE_KEY + "extraInfo.businessPlan"),
        sorter: false,
        render: (data) => {
          const updatedData = { ...data };
          updatedData.businessPlan =
            businessPlanMapper[updatedData.businessPlan];
          return checkNonMoneyDataForTable(updatedData, "businessPlan");
        },
      });
    }
    return <Datatable columns={extraInfoColumns} datasource={data} />;
  };

  const handleOnboardAccount = async (data) => {
    const uniqueId = getRowUniqueId(data);
    setIsAccountActionLoading((prev) => {
      const newState = { ...prev, [uniqueId]: true };
      return newState;
    });

    try {
      const onboardResponse = await postProcessorOnboardMerchant(
        merchantId,
        data.processor,
        data.onboardType,
      );

      if (onboardResponse.data?.responseCode === "0000") {
        actionSnackBar("onboard", "success", { processor: data.processor });
        await getProcessorOnboardStatus(merchantId);
        // Notify Processor & MDR page to refresh
        notifyMdrRefresh();
      } else {
        throw new Error(onboardResponse.data?.message);
      }
    } catch (error) {
      actionSnackBar("onboard", "failed", {
        processor: data.processor,
        errorMessage: error.message ?? "unknown error",
      });
    } finally {
      await fetchProcessorOnboardStatus();
      setIsAccountActionLoading((prev) => {
        const newState = { ...prev };
        delete newState[uniqueId];
        return newState;
      });
    }
  };

  const handleEditAccount = async (data) => {
    const uniqueId = getRowUniqueId(data);
    setIsEditing(uniqueId);
    setMid(data?.mid);
  };

  const handleSaveAccount = async (data) => {
    const uniqueId = getRowUniqueId(data);
    setIsAccountActionLoading((prev) => ({ ...prev, [uniqueId]: true }));
    const payload = {
      mid,
      merchantId,
      paymentGateway: data?.processor,
    };

    try {
      const response = await batchUpdateGsi(payload);
      const success = response?.data?.responseCode === "0000";
      // Notify Processor & MDR page to refresh on successful save
      if (success) {
        actionSnackBar("save", "success", { processor: data.processor });
        notifyMdrRefresh();
      } else {
        throw new Error(response.data?.message);
      }
    } catch (error) {
      actionSnackBar("save", "failed", {
        processor: data.processor,
        errorMessage: error.message ?? "unknown error",
      });
    } finally {
      setIsAccountActionLoading((prev) => {
        const newState = { ...prev };
        delete newState[uniqueId];
        return newState;
      });
      setIsEditing("");
      await fetchProcessorOnboardStatus();
    }
  };

  const handleOffboardAccount = async (data) => {
    const uniqueId = getRowUniqueId(data);
    setIsAccountActionLoading((prev) => {
      const newState = { ...prev, [uniqueId]: true };
      return newState;
    });

    try {
      const offboardResponse = await delProcessorOffboardMerchant(
        merchantId,
        data.processor,
      );
      if (offboardResponse.data?.responseCode === "0000") {
        actionSnackBar("offboard", "success", { processor: data.processor });
        // Notify Processor & MDR page to refresh
        notifyMdrRefresh();
      } else {
        throw new Error(offboardResponse.data?.message);
      }
    } catch (error) {
      actionSnackBar("offboard", "failed", {
        processor: data.processor,
        errorMessage: error.message ?? "unknown error",
      });
    } finally {
      await fetchProcessorOnboardStatus();
      setIsAccountActionLoading((prev) => {
        const newState = { ...prev };
        delete newState[uniqueId];
        return newState;
      });
    }
  };

  const openOffboardConfirm = (data) => {
    setOffboardTarget(data);
    setOffboardDialogParams({
      open: true,
      content: t(PAGE_LOCALE_KEY + "dialog.content.offboardConfirm", {
        processor: data?.processor,
      }),
      title: t(PAGE_LOCALE_KEY + "dialog.title.offboardConfirm"),
      positiveAction: t(PAGE_LOCALE_KEY + "dialog.action.confirm"),
      negativeAction: t(PAGE_LOCALE_KEY + "dialog.action.cancel"),
    });
  };

  const confirmOffboard = async () => {
    if (!offboardTarget) return;
    try {
      setIsOffboardDialogLoading(true);
      await handleOffboardAccount(offboardTarget);
    } finally {
      setIsOffboardDialogLoading(false);
      setOffboardDialogParams(initialOffboardDialogState);
      setOffboardTarget(null);
    }
  };

  const cancelOffboard = () => {
    setOffboardDialogParams(initialOffboardDialogState);
    setOffboardTarget(null);
  };

  const handleReinstateAccount = () => {
    setIsDialogLoading(false);
    setDialogParams({
      open: true,
      content: generateReinstateDialog(),
      title: t(PAGE_LOCALE_KEY + "dialog.title.reinstate"),
      positiveAction: t(PAGE_LOCALE_KEY + "dialog.action.confirm"),
      negativeAction: t(PAGE_LOCALE_KEY + "dialog.action.cancel"),
    });
  };

  const generateReinstateDialog = () => {
    return (
      <div>
        <p>{t(PAGE_LOCALE_KEY + "dialog.content.reinstateType")}</p>
        <Select
          defaultValue={selectedReinstateType}
          sx={{ width: "100%" }}
          onChange={(e) => setSelectedReinstateType(e.target.value)}
        >
          <MenuItem value="amex-mid-sme">
            MID 1(SME): {config.AMEX_BBMSL_MID}
          </MenuItem>
          <MenuItem value="amex-mid-standard">
            MID 2(STANDARD): {config.AMEX_BBMSL_MID_2}
          </MenuItem>
        </Select>
      </div>
    );
  };

  const handleConfirmReinstate = async () => {
    const onboardType =
      selectedReinstateType === "amex-mid-sme"
        ? "MAS_AMEX_FIRST_REQ"
        : "MAS_AMEX_SECOND_REQ";
    const onboardParams = {
      processor: "AMEX_BBMSL",
      onboardType,
    };
    try {
      setIsDialogLoading(true);
      await handleOnboardAccount(onboardParams);
    } finally {
      setIsDialogLoading(false);
      handleCancelReinstate();
    }
  };

  const handleCancelReinstate = () => {
    setDialogParams(initialDialogState);
    setSelectedReinstateType("amex-mid-sme");
    setIsDialogLoading(false);
  };

  const handleActionBtn = (data) => {
    const uniqueId = getRowUniqueId(data);
    const isActionLoading = isAccountActionLoading[uniqueId];
    // Disable edit button if any action is loading for this specific row OR if this specific row is being edited
    const isDisabled = isActionLoading || isEditing;

    // AMEX mode guardrails - check if there's a conflict between AMEX processors
    const isAmexConflict =
      (amexMode === ProcessorType.AMEX_BBMSL &&
        data.processor === ProcessorType.AMEX) ||
      (amexMode === ProcessorType.AMEX &&
        data.processor === ProcessorType.AMEX_BBMSL);

    if (isAmexConflict) {
      return (
        <div className={s.notSupportedContainer}>
          {t(PAGE_LOCALE_KEY + "button.offboardBeforeOnboard", {
            processor1: amexMode,
            processor2: data.processor,
          })}
        </div>
      );
    }
    const loadingSpinner = (
      <CircularProgress
        className={s.spinningIcon}
        size={20}
        sx={{ color: "#36b3be" }}
      />
    );

    // Editing state actions
    if (isEditing === uniqueId) {
      return (
        <Stack
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          flexWrap={"wrap"}
          marginLeft={"10px"}
        >
          <Button
            sx={{
              alignSelf: "flex-end",
              display: !isEditing ? "" : "block",
              visibility: !isEditing ? "hidden" : "",
            }}
            variant="outline-normal-button"
            onClick={() => setIsEditing("")}
            type="button"
            disabled={isActionLoading}
          >
            {t(PAGE_LOCALE_KEY + "button.cancel")}
          </Button>
          <LoadingButton
            variant="normal-button"
            sx={{
              alignSelf: "flex-end",
              display: !isEditing ? "" : "block",
              visibility: !isEditing ? "hidden" : "",
            }}
            loading={isActionLoading}
            loadingPosition="center"
            loadingIndicator={loadingSpinner}
            onClick={async () => await handleSaveAccount(data)}
          >
            {t(PAGE_LOCALE_KEY + "button.save")}
          </LoadingButton>
        </Stack>
      );
    }
    const renderActionButton = (onClick, labelKey, showLoading = true) => (
      <button className={s.actionBtn} disabled={isDisabled} onClick={onClick}>
        {showLoading && isActionLoading
          ? loadingSpinner
          : t(PAGE_LOCALE_KEY + labelKey)}
      </button>
    );

    const renderNotSupported = (labelKey) => (
      <div className={s.notSupportedContainer}>
        {t(PAGE_LOCALE_KEY + labelKey)}
      </div>
    );

    const buttonArray = [];
    if (
      data.processor === ProcessorType.FUBON &&
      data.cardTypes.includes("UNIONPAY")
    ) {
      return <div className={s.notSupportedContainer}>-</div>;
    }
    switch (data.status) {
      case "ONBOARD_FAILED":
      case "UPDATE_FAILED":
      case "NOT_ONBOARD": {
        if (!NOT_ALLOW_ONBOARD_PROCESSOR.includes(data.processor)) {
          const labelKey = {
            ONBOARD_FAILED: "button.retryOnboard",
            UPDATE_FAILED: "button.retryUpdate",
            NOT_ONBOARD: "button.onboard",
          }[data.status];
          buttonArray.push(
            renderActionButton(() => handleOnboardAccount(data), labelKey),
          );
        }
        break;
      }
      case "ONBOARDED": {
        if (
          [
            ProcessorType.HSBC,
            ProcessorType.YEDPAY,
            ProcessorType.AMEX_BBMSL,
            ProcessorType.AMEX,
          ].includes(data.processor) &&
          !data.cardTypes.includes("UNIONPAY")
        ) {
          buttonArray.push(
            renderActionButton(
              () => openOffboardConfirm(data),
              "button.offboard",
            ),
          );
        } else {
          buttonArray.push(renderNotSupported("button.offboardNotSupported"));
        }
        break;
      }
      case "OFFBOARDED": {
        if ([ProcessorType.AMEX_BBMSL].includes(data.processor)) {
          buttonArray.push(
            renderActionButton(handleReinstateAccount, "button.reinstate"),
          );
        }
        break;
      }
      default: {
        buttonArray.push(<div className={s.notSupportedContainer}>-</div>);
        break;
      }
    }

    if (
      !NOT_ALLOW_EDIT_PROCESSOR.includes(data.processor) &&
      !data.cardTypes.includes("UNIONPAY")
    ) {
      buttonArray.push(
        renderActionButton(() => handleEditAccount(data), "button.edit", false),
      );
    }

    return <div className={s.actionBtnContainer}>{buttonArray}</div>;
  };

  const handleDisplayCardTypes = (data) => {
    return (
      <div className={s.cardTypesContainer}>
        {data.cardTypes.map((cardType) => {
          const cardTypeKey = cardType?.toLowerCase?.() || "unknown";
          return (
            <img
              key={cardTypeKey}
              src={getCardTypeIcon(cardType)}
              height={24}
              alt={cardTypeKey.toUpperCase()}
              className={s.cardTypeImage}
            />
          );
        })}
      </div>
    );
  };

  const handleShowFailureReasonDialog = (data) => {
    const action = data.failureReason?.action;
    setDialogParams({
      open: true,
      content:
        data?.failureReason?.message ??
        t(PAGE_LOCALE_KEY + "dialog.message.undefined"),
      title: t(PAGE_LOCALE_KEY + `dialog.title.failureReason.${action}`),
    });
  };

  const handleShowExtraInfoDialog = (data) => {
    setDialogParams({
      open: true,
      content: generateExtraInfoTable(data.extraInfo),
      title: t(PAGE_LOCALE_KEY + "dialog.title.extraInfo"),
    });
  };

  const fetchProcessorOnboardStatus = async () => {
    try {
      const res = await getProcessorOnboardStatus(merchantId);
      if (res.data?.responseCode === "0000") {
        setStatusList(res.data);
        setAmexMode(deriveAmexMode(res.data?.data));
        processorAccountsCache[merchantId] = res.data;
        const now = Date.now();
        setLastUpdated(now);
        processorAccountsCache[merchantId].lastUpdated = now;
      } else {
        throw new Error(res.data?.message);
      }
    } catch (error) {
      actionSnackBar("fetchProcessorStatus", "failed", {
        errorMessage: error.message ?? "unknown error",
      });
    } finally {
      fetchMerchantDetail();
      setIsLoading(false);
    }
  };

  const fetchAllinPayMultipleMid = async () => {
    try {
      const res = await checkAllinPayMultipleMid(merchantId);
      if (res.data?.responseCode === "0000") {
        setIsMultipleAipMerchant(res.data?.data?.isMultipleAipMerchant);
      } else {
        throw new Error(res.data?.message);
      }
    } catch (error) {
      actionSnackBar("fetchAllinPay", "failed", {
        errorMessage: error.message ?? "unknown error",
      });
    }
  };

  // Listen for MDR refresh triggers
  useEffect(() => {
    if (!merchantId) return;

    if (detailPageState[path]?.mdrRefreshTrigger) {
      handleRefresh();
      setDetailPageState((prev) => ({
        ...prev,
        [path]: { ...prev[path], mdrRefreshTrigger: null },
      }));
    } else if (processorAccountsCache[merchantId]) {
      setStatusList(processorAccountsCache[merchantId]);
      setAmexMode(deriveAmexMode(processorAccountsCache[merchantId]?.data));
      setLastUpdated(processorAccountsCache[merchantId]?.lastUpdated ?? null);
      setIsLoading(false);
    } else {
      // Cache is undefined, refresh
      handleRefresh();
    }
  }, [merchantId, detailPageState[path]?.mdrRefreshTrigger]);

  const handleRefresh = async () => {
    setIsLoading(true);
    delete processorAccountsCache[merchantId];
    await Promise.all([
      fetchAllinPayMultipleMid(),
      fetchProcessorOnboardStatus(),
    ]);
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{ width: "100%", mb: 1, gap: 1 }}
        >
          <div className={s.noticeBoxContainer}>
            <NoticeBox>
              <Typography variant="content">
                {t(PAGE_LOCALE_KEY + "noticeBox.processorAccounts")}
              </Typography>
            </NoticeBox>
          </div>
          <Stack
            direction="column"
            alignItems="flex-end"
            justifyContent="center"
            spacing={0.5}
            sx={{
              display: "flex",
              alignSelf: { xs: "flex-start", sm: "stretch" },
            }}
          >
            <ButtonMAS
              variant="bordered"
              size="table-bar-button"
              isLoading={isLoading}
              disabled={isLoading}
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              {t(PAGE_LOCALE_KEY + "button.refresh", {
                defaultValue: "Refresh",
              })}
            </ButtonMAS>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: "#666", whiteSpace: "nowrap", fontSize: "10px" }}
            >
              {lastUpdated
                ? `${t(PAGE_LOCALE_KEY + "lastUpdated", { defaultValue: "Last updated" })}: ${formatTime(lastUpdated)}`
                : t(PAGE_LOCALE_KEY + "neverUpdated", { defaultValue: "-" })}
            </Typography>
          </Stack>
        </Stack>
        {isLoading ? (
          spinning()
        ) : (
          <>
            {statusList && (
              <>
                <Datatable columns={columns} datasource={statusList?.data} />
                <TableFooter
                  sections={FOOTER_SECTIONS.map((s) => ({
                    key: s.key,
                    infomation: `[{key}] ${s.label}`,
                  }))}
                />
              </>
            )}
          </>
        )}

        {dialogParams.open && (
          <SimpleDialog
            open={dialogParams.open}
            dialogOncloseFn={handleCancelReinstate}
            title={dialogParams.title}
            content={dialogParams.content}
            positiveAction={dialogParams.positiveAction}
            positiveActionLoading={isDialogLoading}
            negativeAction={dialogParams.negativeAction}
            negativeActionDisabled={isDialogLoading}
            confirmFn={handleConfirmReinstate}
            cancelFn={handleCancelReinstate}
          ></SimpleDialog>
        )}

        {offboardDialogParams.open && (
          <SimpleDialog
            open={offboardDialogParams.open}
            dialogOncloseFn={cancelOffboard}
            title={offboardDialogParams.title}
            content={offboardDialogParams.content}
            positiveAction={offboardDialogParams.positiveAction}
            positiveActionLoading={isOffboardDialogLoading}
            negativeAction={offboardDialogParams.negativeAction}
            negativeActionDisabled={isOffboardDialogLoading}
            confirmFn={confirmOffboard}
            cancelFn={cancelOffboard}
          ></SimpleDialog>
        )}
      </div>
    </div>
  );
};

export default React.memo(OfflineMerchantProcessorAccounts);
